'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useTheme } from '@/context/ThemeContext';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Mission Map', href: '/', icon: 'ChartBarIcon' },
  { label: 'All Proposals', href: '/proposals', icon: 'RectangleStackIcon', badge: 4 },
  { label: 'Customers', href: '/customer-management', icon: 'UserGroupIcon' },
  { label: 'Vendors', href: '/vendor-management', icon: 'BuildingOffice2Icon' },
  { label: 'Settings', href: '/settings', icon: 'Cog6ToothIcon' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(href + '/');
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-full flex flex-col z-30 sidebar-transition ${
        collapsed ? 'w-16' : 'w-60'
      }`}
      style={{
        background: isLight
          ? 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
          : 'linear-gradient(180deg, #060C18 0%, #080F1E 100%)',
        borderRight: isLight ? '1px solid #E2E8F0' : '1px solid rgba(30, 144, 255, 0.15)',
        transition: 'background 300ms ease, border-color 300ms ease',
      }}
    >
      {/* Logo */}
      <div
        className={`flex items-center h-14 px-3 ${collapsed ? 'justify-center' : 'gap-2'}`}
        style={{ borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(30, 144, 255, 0.15)' }}
      >
        <div className="relative">
          <AppLogo size={32} />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full" style={{ border: isLight ? '1px solid #FFFFFF' : '1px solid #060C18' }} />
        </div>
        {!collapsed && (
          <div>
            <span className="font-bold text-sm tracking-tight truncate block" style={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>ProposalLink</span>
            <span className="text-[9px] font-600 tracking-widest uppercase" style={{ color: '#1E90FF' }}>WAR ROOM</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 flex flex-col gap-0.5">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-2.5 px-2 py-2.5 rounded-lg transition-all duration-200 group relative ${
                active ? 'nav-item-active' : ''
              }`}
              style={!active ? { color: isLight ? '#64748B' : '#6B7FA3' } : undefined}
              onMouseEnter={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = isLight ? '#EFF6FF' : '#0D1829';
                  (e.currentTarget as HTMLElement).style.color = isLight ? '#0F172A' : '#FFFFFF';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = isLight ? '#64748B' : '#6B7FA3';
                }
              }}
            >
              <Icon
                name={item.icon as any}
                size={18}
                variant={active ? 'solid' : 'outline'}
                className={active ? 'text-[#1E90FF]' : ''}
                style={!active ? { color: isLight ? '#64748B' : '#6B7FA3' } as React.CSSProperties : undefined}
              />
              {!collapsed && (
                <span className={`text-sm truncate flex-1 font-500 ${active ? 'text-[#1E90FF] font-600' : ''}`}>
                  {item.label}
                </span>
              )}
              {!collapsed && item.badge && item.badge > 0 && (
                <span
                  className="text-white text-[9px] font-700 px-1.5 py-0.5 rounded-full leading-none"
                  style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)', boxShadow: '0 0 8px rgba(245,158,11,0.4)' }}
                >
                  {item.badge}
                </span>
              )}
              {collapsed && item.badge && item.badge > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ background: '#F59E0B', boxShadow: '0 0 6px rgba(245,158,11,0.6)' }} />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Operator Profile */}
      <div style={{ borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(30, 144, 255, 0.15)' }} className="p-2">
        {!collapsed && (
          <div className="px-2 py-2 mb-2 rounded-lg" style={{
            background: 'rgba(30, 144, 255, 0.06)',
            border: isLight ? '1px solid rgba(30, 144, 255, 0.15)' : '1px solid rgba(30, 144, 255, 0.12)',
          }}>
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-700 shrink-0"
                style={{ background: 'linear-gradient(135deg, #1E90FF, #0066CC)', boxShadow: '0 0 10px rgba(30,144,255,0.4)' }}
              >
                AM
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-600 truncate" style={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>Ahmad Mansour</p>
                <p className="text-[9px] truncate" style={{ color: '#1E90FF' }}>⚡ Proposal Manager · Lv.12</p>
              </div>
              <span className="text-[9px] font-700 px-1.5 py-0.5 rounded" style={{ background: 'rgba(245,158,11,0.2)', color: '#F59E0B' }}>🔥3</span>
            </div>
            <div className="xp-bar">
              <div className="xp-fill" style={{ width: '68%' }} />
            </div>
            <p className="text-[9px] mt-1" style={{ color: isLight ? '#64748B' : '#6B7FA3' }}>2,840 / 4,200 XP · Next: Senior Operator</p>
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center mb-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-700"
              style={{ background: 'linear-gradient(135deg, #1E90FF, #0066CC)', boxShadow: '0 0 10px rgba(30,144,255,0.4)' }}
            >
              AM
            </div>
          </div>
        )}
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg transition-all duration-150"
          style={{ color: isLight ? '#64748B' : '#6B7FA3' }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background = isLight ? '#EFF6FF' : '#0D1829';
            (e.currentTarget as HTMLElement).style.color = isLight ? '#0F172A' : '#FFFFFF';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = 'transparent';
            (e.currentTarget as HTMLElement).style.color = isLight ? '#64748B' : '#6B7FA3';
          }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'} size={15} />
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}